/**
 * Запросы и хуки аутентификации (docs/06-api.md, раздел «Аутентификация»).
 *
 * Ответы проверяются схемами Zod на границе: если сервер и клиент разъедутся
 * по контракту, это должно быть видно сразу, а не в виде undefined в разметке
 * (docs/12-development.md).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { ApiError, apiFetch } from './client'
import { authFetch, clearSession, refreshSession, setAccessToken } from './session'

export const userSchema = z.object({
  id: z.string(),
  email: z.string(),
  display_name: z.string().nullable(),
  role: z.enum(['user', 'author', 'admin']),
  created_at: z.string(),
})

export const authResponseSchema = z.object({
  access_token: z.string(),
  expires_in: z.number(),
  user: userSchema,
})

export type User = z.infer<typeof userSchema>
export type AuthResponse = z.infer<typeof authResponseSchema>

export const currentUserKey = ['auth', 'me'] as const

export interface RegisterInput {
  email: string
  password: string
  displayName?: string
}

export interface LoginInput {
  email: string
  password: string
}

async function postCredentials(path: string, body: unknown): Promise<AuthResponse> {
  const response = await apiFetch<unknown>(path, {
    method: 'POST',
    body: JSON.stringify(body),
  })
  const parsed = authResponseSchema.parse(response)
  // Токен сразу уходит в память модуля: дальше все запросы идут с ним.
  setAccessToken(parsed.access_token)
  return parsed
}

export function register(input: RegisterInput): Promise<AuthResponse> {
  return postCredentials('/auth/register', {
    email: input.email,
    password: input.password,
    display_name: input.displayName?.trim() || null,
  })
}

export function login(input: LoginInput): Promise<AuthResponse> {
  return postCredentials('/auth/login', { email: input.email, password: input.password })
}

export async function logout(): Promise<void> {
  try {
    await apiFetch<void>('/auth/logout', { method: 'POST' })
  } finally {
    // Локальную сессию забываем в любом случае: если сервер не ответил,
    // пользователь всё равно ожидает, что вышел.
    clearSession()
  }
}

/**
 * Текущий пользователь. Гость — это `null`, а не ошибка: 401 здесь
 * нормальный ответ, и показывать пользователю сообщение об ошибке не нужно.
 */
export async function fetchCurrentUser(): Promise<User | null> {
  try {
    return userSchema.parse(await authFetch<unknown>('/auth/me'))
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      return null
    }
    throw error
  }
}

export function useCurrentUser() {
  return useQuery({
    queryKey: currentUserKey,
    queryFn: fetchCurrentUser,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      queryClient.setQueryData(currentUserKey, data.user)
    },
  })
}

export function useRegister() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: register,
    onSuccess: (data) => {
      queryClient.setQueryData(currentUserKey, data.user)
    },
  })
}

export interface ChangePasswordInput {
  /** Не нужен аккаунту, заведённому через провайдера: пароля у него нет. */
  currentPassword?: string | undefined
  newPassword: string
}

/**
 * Смена пароля (FR-A7) или задание первого (FR-A13). Прочие сессии сервер
 * отзывает сам, а выданный ранее access-токен перестаёт действовать сразу —
 * поэтому после успеха сессия обновляется по refresh-cookie, иначе следующий
 * запрос получил бы 401.
 */
export async function changePassword(input: ChangePasswordInput): Promise<void> {
  await authFetch<void>('/auth/password', {
    method: 'POST',
    body: JSON.stringify({
      current_password: input.currentPassword ?? null,
      new_password: input.newPassword,
    }),
  })
  await refreshSession()
}

export function useChangePassword() {
  return useMutation({ mutationFn: changePassword })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: logout,
    onSettled: () => {
      queryClient.setQueryData(currentUserKey, null)
      // Прогресс, подсказки и прочее принадлежат прежнему пользователю.
      void queryClient.invalidateQueries()
    },
  })
}
